import { useRef, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Typography,
} from '@mui/material'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { QRCodeCanvas } from 'qrcode.react'

import { APP_NAME } from '../lib/branding'
import { downloadPdf } from '../lib/printHtml'
import { useOrgSettings } from '../hooks/useOrgSettings'

/**
 * QR payload format mirrors `apps/mobile/lib/core/qr/qr_payload.dart`.
 * Encoded as HTTPS URLs on `cmms.bacsys.net` so the system camera,
 * mail, browsers, etc. recognise them via Android App Links / iOS
 * Universal Links — the app opens automatically when installed,
 * otherwise the URL falls through to the web app.
 */
const QR_HOST = 'https://cmms.bacsys.net'

export const qrPayload = {
  asset: (id: string) => `${QR_HOST}/assets/${id}`,
  workOrder: (id: string) => `${QR_HOST}/work-orders/${id}`,
  location: (siteId: string, id: string) => `${QR_HOST}/locations/${siteId}/${id}`,
}

interface Props {
  open: boolean
  onClose: () => void
  payload: string
  title: string        // e.g. asset name
  kind: string         // 'Asset' | 'Work order' | 'Location'
  subtitle?: string    // e.g. asset code / WO code
}

export function QrCodeDialog({ open, onClose, payload, title, kind, subtitle }: Props) {
  const cardRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<HTMLImageElement>(null)
  const [busy, setBusy] = useState(false)
  const { prefs } = useOrgSettings()
  const qr = prefs.qr ?? {}
  const companyName    = qr.company_name?.trim() || ''
  const tagline        = qr.tagline?.trim() || ''
  // HSE fork: default footer copy points members of the public at a
  // camera / QR scanner and calls out the intent (report a hazard).
  // The old "mobile app" copy came from CMMS where the QR was a
  // deep-link into the tech app; HSE stickers open a browser form.
  const footerText     = qr.footer_text?.trim() || 'Scan with your camera or a QR scanner to report a hazard.'
  const logoUrl        = qr.logo_url ?? null
  const fgColor        = qr.fg_color || '#111827'
  const bgColor        = qr.bg_color || '#FFFFFF'
  const errorCorrection: 'L'|'M'|'Q'|'H' = qr.error_correction || 'M'

  /**
   * Compose the full printable card (logo + brand + QR + title + subtitle)
   * into a single off-screen canvas. The on-screen card uses HTML; here
   * we redraw with the same geometry so PNG / PDF outputs match.
   */
  function composeCardCanvas(): HTMLCanvasElement | null {
    const qrCanvas = cardRef.current?.querySelector('canvas') as HTMLCanvasElement | null
    if (!qrCanvas) return null

    const scale = 2 // crisp on print and retina screens
    const W = 360 * scale

    // Vertical layout: company → tagline → QR → title → subtitle →
    // footer → logo. Each row's height collapses to 0 when its content
    // is missing, so the card snugly fits whatever's configured.
    const padTop = 30 * scale
    const brandH = companyName ? 24 * scale : 0
    const taglineH = tagline ? 18 * scale : 0
    const brandGap = (companyName || tagline) ? 16 * scale : 0
    const qrSize = 280 * scale
    const qrGap = 24 * scale
    const titleH = 22 * scale
    const subtitleH = subtitle ? 18 * scale : 0
    const footerGap = footerText ? 16 * scale : 0
    const footerH = footerText ? 18 * scale : 0
    const logoGap = logoUrl ? 14 * scale : 0
    const logoH = logoUrl ? 44 * scale : 0
    const padBot = 28 * scale

    const H = padTop + brandH + taglineH + brandGap
            + qrSize + qrGap + titleH + subtitleH
            + footerGap + footerH + logoGap + logoH + padBot

    const out = document.createElement('canvas')
    out.width = W; out.height = H
    const ctx = out.getContext('2d')
    if (!ctx) return null

    ctx.fillStyle = bgColor
    ctx.fillRect(0, 0, W, H)

    let y = padTop

    // Company name
    if (companyName) {
      ctx.fillStyle = fgColor
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.font = `800 ${15 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      ctx.fillText(truncate(companyName, 36), W / 2, y)
      y += brandH
    }
    // Tagline
    if (tagline) {
      ctx.fillStyle = withAlpha(fgColor, 0.7)
      ctx.font = `500 ${11 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      ctx.fillText(truncate(tagline, 56), W / 2, y)
      y += taglineH
    }
    if (companyName || tagline) y += brandGap - 4 * scale

    // QR — square, centered horizontally
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(qrCanvas, (W - qrSize) / 2, y, qrSize, qrSize)
    y += qrSize + qrGap

    // Title
    ctx.fillStyle = fgColor
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.font = `800 ${18 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
    ctx.fillText(truncate(title, 32), W / 2, y)
    y += titleH

    // Subtitle
    if (subtitle) {
      ctx.fillStyle = withAlpha(fgColor, 0.6)
      ctx.font = `500 ${13 * scale}px ui-monospace, SFMono-Regular, Menlo, monospace`
      ctx.fillText(truncate(subtitle, 40), W / 2, y)
      y += subtitleH
    }

    // Footer
    if (footerText) {
      y += footerGap
      ctx.fillStyle = withAlpha(fgColor, 0.55)
      ctx.font = `500 ${10.5 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      ctx.fillText(truncate(footerText, 60), W / 2, y)
      y += footerH
    }

    // Logo (centered, below the footer)
    if (logoUrl && logoRef.current?.complete && logoRef.current.naturalWidth > 0) {
      const imgEl = logoRef.current
      const naturalRatio = imgEl.naturalWidth / imgEl.naturalHeight
      const maxW = 180 * scale
      let drawH = logoH
      let drawW = drawH * naturalRatio
      if (drawW > maxW) { drawW = maxW; drawH = drawW / naturalRatio }
      y += logoGap
      ctx.drawImage(imgEl, (W - drawW) / 2, y, drawW, drawH)
    }

    return out
  }

  function downloadPng() {
    const canvas = composeCardCanvas()
    if (!canvas) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = `${safeFilename(title)}-qr.png`
    a.click()
  }

  async function print() {
    setBusy(true)
    try {
      const canvas = composeCardCanvas()
      if (!canvas) return
      const dataUrl = canvas.toDataURL('image/png')
      await downloadPdf(`<!doctype html>
<html><head><title>${escapeHtml(title)} — QR</title>
<style>
  html, body { margin: 0; padding: 0; background: #fff; }
  body { display: flex; align-items: center; justify-content: center; padding: 32px; box-sizing: border-box; }
  img { max-width: 100%; height: auto; }
</style>
</head><body>
<img src="${dataUrl}" alt="${escapeHtml(title)} QR code" />
</body></html>`, `${safeFilename(title)}-qr.pdf`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{kind} QR code</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Stack alignItems="center" spacing={2}>
          <Box ref={cardRef} className="qr-card" sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2.5,
            px: 3, py: 3, bgcolor: bgColor, textAlign: 'center', minWidth: 280,
          }}>
            {companyName && (
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: fgColor, mb: 0.25 }}>
                {companyName}
              </Typography>
            )}
            {tagline && (
              <Typography sx={{ fontSize: 11, color: fgColor, opacity: 0.7, mb: 1 }}>
                {tagline}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: (companyName || tagline) ? 1.25 : 0 }}>
              <QRCodeCanvas
                value={payload} size={220} level={errorCorrection}
                fgColor={fgColor} bgColor={bgColor} />
            </Box>
            <Typography sx={{
              mt: 1.5, fontSize: 14, fontWeight: 800,
              color: fgColor,
              maxWidth: 240, mx: 'auto',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{title}</Typography>
            {subtitle && (
              <Typography sx={{
                fontSize: 11, fontFamily: 'ui-monospace, monospace', color: fgColor, opacity: 0.6,
              }}>{subtitle}</Typography>
            )}
            {footerText && (
              <Typography sx={{ fontSize: 10.5, color: fgColor, opacity: 0.55, mt: 1.25 }}>
                {footerText}
              </Typography>
            )}
            {logoUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                <img ref={logoRef} src={logoUrl} alt="" crossOrigin="anonymous"
                  style={{ maxHeight: 36, maxWidth: 180, objectFit: 'contain' }} />
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} size="small">Close</Button>
        <Button onClick={downloadPng} size="small" variant="outlined"
          startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}>
          Download PNG
        </Button>
        <Button onClick={print} size="small" variant="contained"
          disabled={busy}
          startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}>
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function safeFilename(s: string): string {
  const cleaned = s.replace(/[^A-Za-z0-9 _-]/g, '').replace(/\s+/g, '-').toLowerCase()
  return cleaned || 'cmms'
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!))
}

function withAlpha(hex: string, a: number): string {
  // Accepts #RGB / #RRGGBB. Falls back to the input if it's not parseable.
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return hex
  let r = 0, g = 0, b = 0
  const h = m[1]!
  if (h.length === 3) {
    r = parseInt(h[0]! + h[0]!, 16); g = parseInt(h[1]! + h[1]!, 16); b = parseInt(h[2]! + h[2]!, 16)
  } else {
    r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16)
  }
  return `rgba(${r}, ${g}, ${b}, ${a})`
}
